import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IUnplannedRequest,
  IUnplannedRequestEvent,
  IUnplannedRequestEventSP,
  IUnplannedRequestRules,
} from "online-services-types";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { APIFetch, APIFetchStatus } from "src/APIFetch";
import { QueueStatus } from "src/components/AttachmentUploader";
import { IUploadedFile } from "src/components/AttachmentUploader/AttachmentUploaderComponent";
import { IAppState } from "src/redux";
import { APIResourceState } from "src/redux/api";
import {
  IUnplannedRequestFiltersDispatchProps,
  IUnplannedRequestFiltersStateProps,
  UnplannedRequestFilters,
  UnplannedRequestQueryParams,
} from "./unplannedRequest.types";
import { getQueryParamsFromFilters } from "./unplannedRequest.utils";

export const UNPLANNED_REQUESTS = "unplannedRequests";
const PRESELECTED_UNPLANNED_REQUEST = "preselectedUnplannedRequest";
const UNPLANNED_REQUEST_RULES = "unplannedRequestRules";
const UNPLANNED_REQUEST = "unplannedRequest";
const UNPLANNED_REQUEST_EVENTS = "unplannedRequestEvents";
const UNPLANNED_REQUEST_EVENT_SPARE_PARTS = "unplannedRequestEventSpareParts";
export const UNPLANNED_REQUEST_EVENT_ATTACHMENTS = "unplannedRequestEventAttachments";

const getUnplannedRequest = async (id: string): Promise<IUnplannedRequest | undefined> => {
  return (await new APIFetch<IUnplannedRequest>("contracts/unplanned-requests").get(id)) as
    | IUnplannedRequest
    | undefined;
};

const getUnplannedRequestEvents = async (id: string): Promise<IUnplannedRequestEvent[]> => {
  const result = await new APIFetch<IUnplannedRequestEvent[]>("contracts/unplanned-requests", "events").get(id);
  return (result as unknown as IUnplannedRequestEvent[]) || [];
};

export const useUnplannedRequest = (id?: string) =>
  useQuery([UNPLANNED_REQUEST, id], () => getUnplannedRequest(id!), { enabled: Boolean(id), cacheTime: 0 });

export const useUnplannedRequestEvents = (id?: string) =>
  useQuery([UNPLANNED_REQUEST_EVENTS, id], () => getUnplannedRequestEvents(id!), {
    enabled: Boolean(id),
    cacheTime: 0,
  });

/**
 * Fetches spare parts for every event of a given unplanned request.
 * Returns an array (one entry per event) of IUnplannedRequestEventSP[].
 * If no events are supplied yet the result is an empty array.
 */
const getUnplannedRequestEventSpareParts = async (
  events: IUnplannedRequestEvent[]
): Promise<IUnplannedRequestEventSP[][]> => {
  return await Promise.all(
    events.map(async (event) => {
      try {
        const result = await new APIFetch<IUnplannedRequestEventSP[]>(
          "contracts/unplanned-requests/events",
          "spare-parts"
        ).get(event.id);
        return (result as unknown as IUnplannedRequestEventSP[]) || [];
      } catch {
        return [];
      }
    })
  );
};

export const useUnplannedRequestEventSpareParts = (id?: string, events?: IUnplannedRequestEvent[]) =>
  useQuery(
    [UNPLANNED_REQUEST_EVENT_SPARE_PARTS, id, events?.map((e) => e.id).join(",")],
    () => getUnplannedRequestEventSpareParts(events!),
    { enabled: Boolean(id) && Boolean(events?.length), cacheTime: 0 }
  );

const getUnplannedRequestEventAttachments = async (events: IUnplannedRequestEvent[]): Promise<IUploadedFile[][]> => {
  return await Promise.all(
    events.map(async (event) => {
      try {
        const attachments = await new APIFetch<any[]>("contracts/unplanned-requests", "attachments").get(event.id);
        // The API may return the ContentDocument id (069) as `id`; downloads require the ContentVersion id (068).
        return ((attachments as any[]) || []).map((a) => ({
          ...a,
          id: a.versionId || a.id,
          status: QueueStatus.Success,
        })) as IUploadedFile[];
      } catch {
        return [];
      }
    })
  );
};

export const useUnplannedRequestEventAttachments = (id?: string, events?: IUnplannedRequestEvent[]) =>
  useQuery(
    [UNPLANNED_REQUEST_EVENT_ATTACHMENTS, id, events?.map((e) => e.id).join(",")],
    () => getUnplannedRequestEventAttachments(events!),
    { enabled: Boolean(id) && Boolean(events?.length), cacheTime: 0 }
  );

const getUnplannedRequestRules = async (): Promise<IUnplannedRequestRules[]> => {
  return await new APIFetch<IUnplannedRequestRules[]>("contracts/unplanned-request-rules").get();
};

export const useUnplannedRequestRules = () =>
  useQuery([UNPLANNED_REQUEST_RULES], getUnplannedRequestRules, { staleTime: 5 * 60 * 1000 });

const getUnplannedRequests = async (params: UnplannedRequestQueryParams = {}): Promise<IUnplannedRequest[]> => {
  return await new APIFetch<IUnplannedRequest[]>("contracts/unplanned-requests").getWithParams(params);
};

export const useUnplannedRequests = (filters: UnplannedRequestFilters = {}) => {
  const params = getQueryParamsFromFilters(filters);
  const paramsKey = new URLSearchParams(params).toString();
  const defaultLimit = Number(params.limit || 10);

  return useInfiniteQuery(
    [UNPLANNED_REQUESTS, paramsKey],
    async ({ pageParam = 1 }) => {
      const prevPage = pageParam - 1;
      const firstPage = pageParam === 1;
      const offset = firstPage ? 0 : prevPage * defaultLimit;
      return getUnplannedRequests({ ...params, offset: offset.toString() });
    },
    {
      getNextPageParam: (_, allPages) => {
        const lastPage = allPages[allPages.length - 1];
        const isLastPage = lastPage.length < defaultLimit;

        return isLastPage ? undefined : allPages.length + 1;
      },
    }
  );
};

export const usePreselectedUnplannedRequest = (id?: string, enabled?: boolean) => {
  const params = getQueryParamsFromFilters({ id, limit: 1 });
  return useQuery(
    [PRESELECTED_UNPLANNED_REQUEST, id],
    async () => {
      return getUnplannedRequests(params);
    },
    { enabled }
  );
};

const deleteUnplannedRequest = async (id: string): Promise<void> => {
  await new APIFetch<IUnplannedRequest>("contracts/unplanned-requests").delete(id);
};

export const useDeleteUnplannedRequest = ({ filters = {} }: { filters?: UnplannedRequestFilters }) => {
  const queryClient = useQueryClient();
  const paramsKey = new URLSearchParams(getQueryParamsFromFilters(filters)).toString();

  return useMutation(["DeleteUnplannedRequest"], async (id: string) => deleteUnplannedRequest(id), {
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [UNPLANNED_REQUESTS, paramsKey] });
      queryClient.invalidateQueries({ queryKey: [PRESELECTED_UNPLANNED_REQUEST] });
    },
  });
};

export const useUnplannedRequestFiltersStateProps = (): IUnplannedRequestFiltersStateProps =>
  useSelector<IAppState, IUnplannedRequestFiltersStateProps>((state) => ({
    accounts: state.customerGroupAccounts.data || [],
    installations: state.hierarchyInstallations.data || [],
    equipments: state.equipments.data || [],
    isAccountsReady: state.customerGroupAccounts.status !== APIFetchStatus.Busy,
    isInstallationsReady: state.hierarchyInstallations.status !== APIFetchStatus.Busy,
    isEquipmentsReady: state.equipments.status !== APIFetchStatus.Busy,
  }));

export const useUnplannedRequestFiltersDispatchProps = (): IUnplannedRequestFiltersDispatchProps => {
  const dispatch = useDispatch();

  return bindActionCreators(
    {
      getAccounts: APIResourceState.customerGroupAccounts.get,
      getInstallations: APIResourceState.hierarchyInstallations.get,
      getEquipments: APIResourceState.equipments.get,
    },
    dispatch
  );
};
